'use client';

import type React from 'react';

import { ImageNodeView } from '@/components/image-node-view';
import { MentionExtension } from '@/components/mention-extension';
import { MentionNode } from '@/components/mention-node';
import { Button } from '@/components/ui/button';
import { cn, getPlainTextLength } from '@/lib/utils';
import { uploadToS3 } from '@/services/api/account-settings';
import { getPresignedUrl } from '@/services/api/story';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, ReactNodeViewRenderer, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  ImageIcon,
  Italic,
  Link2Icon,
  List,
  Trash2,
  UnderlineIcon,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import '../../styles/prose-mirror.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  isImageUploadEnabled?: boolean;
  disabled?: boolean;
  hideActions?: boolean;
  height?: string;
  padding?: string;
  limit?: number;
}

// Define the extended attributes for our custom image extension
interface CustomImageAttributes {
  src: string;
  alt?: string;
  title?: string;
  isLoading?: boolean;
  id?: string;
}

// Custom extension to handle image uploads with loading state
const CustomImage = Image.extend({
  name: 'customImage',

  addAttributes() {
    return {
      ...this.parent?.(),
      isLoading: {
        default: false,
        parseHTML: (element) => element.getAttribute('data-loading') === 'true',
        renderHTML: (attributes) => {
          if (!attributes.isLoading) {
            return {};
          }

          return {
            'data-loading': 'true',
          };
        },
      },
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {};
          }

          return {
            'data-id': attributes.id,
          };
        },
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

export function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter background or context of question here...',
  className,
  isImageUploadEnabled = false,
  disabled,
  hideActions,
  height = '120px',
  padding = '16px',
  limit,
}: RichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImages, setUploadingImages] = useState<
    Record<string, boolean>
  >({});
  // State for link modal
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [linkText, setLinkText] = useState('');

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Link.extend({
        inclusive: false,
      }).configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
        autolink: true,
      }),
      Underline,
      CustomImage.configure({
        HTMLAttributes: {
          class: 'max-w-full rounded-md my-4',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      // Make sure MentionNode comes before MentionExtension
      MentionNode.configure({
        HTMLAttributes: {
          class: 'mention',
        },
      }),
      MentionExtension,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `outline-none prose prose-sm max-w-none ${disabled ? 'opacity-70 pointer-events-none' : ''}`,
        style: `min-height: ${height}; padding: ${padding}; ${disabled ? 'background-color: #f1f5f9; cursor: not-allowed;' : ''}`,
      },
      editable: () => !disabled,
    },
  });

  const uploadImage = async (file: File): Promise<string> => {
    const fileType = file.type.split('/')[1];
    if (!fileType) {
      throw new Error('Invalid file type');
    }
    const response = await getPresignedUrl(fileType);
    const { presignedUrl, s3Url } = response.data;
    await uploadToS3(presignedUrl, file);
    return s3Url;
  };

  // Custom link modal logic
  const openLinkModal = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkInput(previousUrl);
    // Get selected text or current link text
    const selection = editor.state.selection;
    let selectedText = '';
    if (!selection.empty) {
      selectedText = editor.state.doc.textBetween(selection.from, selection.to);
    } else {
      // If selection is collapsed, try to get the text of the current link mark
      const pos = selection.from;
      editor.state.doc.nodesBetween(pos, pos, (node) => {
        if (
          node.isText &&
          node.marks.some((mark) => mark.type.name === 'link')
        ) {
          selectedText = node.text || '';
        }
      });
    }
    setLinkText(selectedText);
    setLinkModalOpen(true);
  }, [editor]);

  const closeLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setLinkInput('');
    setLinkText('');
  }, []);

  const handleLinkConfirm = useCallback(() => {
    if (!editor) return;
    let url = linkInput.trim();
    const text = linkText.trim();
    if (!url) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      closeLinkModal();
      return;
    }
    // Add protocol if missing
    if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) {
        url = `mailto:${url}`;
      } else {
        url = `https://${url}`;
      }
    }
    // If text is provided and selection is collapsed, insert text
    const { state } = editor;
    if (text && state.selection.empty) {
      editor.chain().focus().insertContent(text).run();
      // Select the inserted text
      const from = state.selection.from;
      editor
        .chain()
        .setTextSelection({ from, to: from + text.length })
        .run();
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    closeLinkModal();
  }, [editor, linkInput, linkText, closeLinkModal]);

  const handleLinkRemove = useCallback(() => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    closeLinkModal();
  }, [editor, closeLinkModal]);

  const handleImageUpload = useCallback(() => {
    if (!editor || !fileInputRef.current) return;
    fileInputRef.current.click();
  }, [editor]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!editor || !event.target.files || event.target.files.length === 0)
        return;

      const file = event.target.files[0];
      if (!file?.type.startsWith('image/')) return;

      try {
        // Generate a temporary ID for this upload
        const imageId = uuidv4();

        // Create a temporary URL for preview
        const tempUrl = URL.createObjectURL(file);

        // Insert image with loading state - use type assertion to handle custom attributes
        editor
          .chain()
          .focus()
          .setImage({
            src: tempUrl,
            alt: file.name,
            isLoading: true,
            id: imageId,
          } as CustomImageAttributes)
          .run();

        // Track this image as uploading
        setUploadingImages((prev) => ({ ...prev, [imageId]: true }));

        let finalUrl: string;

        try {
          finalUrl = await uploadImage(file);
        } catch {
          toast.error('S3 upload failed');
        }

        // Find the image node and update it with the final URL
        const { state } = editor;
        const { tr } = state;

        state.doc.descendants((node, pos) => {
          if (node.type.name === 'customImage' && node.attrs.id === imageId) {
            tr.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              src: finalUrl,
              isLoading: false,
            });
            return false;
          }
          return true;
        });

        editor.view.dispatch(tr);

        // Remove from uploading images
        setUploadingImages((prev) => {
          const newState = { ...prev };
          delete newState[imageId];
          return newState;
        });

        // Revoke the temporary URL to free up memory
        URL.revokeObjectURL(tempUrl);
      } catch {
        toast.error('Image upload failed. Failed to process the image.');
      }

      // Reset the input so the same file can be selected again
      event.target.value = '';
    },
    [editor]
  );

  if (!editor) {
    return null;
  }

  return (
    <div>
      <div
        className={cn(
          'border rounded-md overflow-hidden',
          disabled && 'opacity-70 cursor-not-allowed',
          className
        )}
      >
        <EditorContent
          suppressContentEditableWarning
          editor={editor}
          style={{
            minHeight: height,
            backgroundColor: disabled ? '#f1f5f9' : undefined,
            cursor: disabled ? 'not-allowed' : undefined,
            overflow: 'auto',
            maxHeight: '300px',
          }}
        />
        {!hideActions && !disabled && (
          <div className="border-t p-2 flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn(
                'h-8 w-8 rounded-md',
                editor.isActive('bold') ? 'bg-muted' : ''
              )}
            >
              <Bold className="h-4 w-4" />
              <span className="sr-only">Bold</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn(
                'h-8 w-8 rounded-md',
                editor.isActive('italic') ? 'bg-muted' : ''
              )}
            >
              <Italic className="h-4 w-4" />
              <span className="sr-only">Italic</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={cn(
                'h-8 w-8 rounded-md',
                editor.isActive('underline') ? 'bg-muted' : ''
              )}
            >
              <UnderlineIcon className="h-4 w-4" />
              <span className="sr-only">Underline</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={openLinkModal}
              className={cn(
                'h-8 w-8 rounded-md',
                editor.isActive('link') ? 'bg-muted' : ''
              )}
            >
              <Link2Icon className="h-4 w-4" />
              <span className="sr-only">Link</span>
            </Button>

            {isImageUploadEnabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleImageUpload}
                className={cn('h-8 w-8 rounded-md')}
              >
                <ImageIcon className="h-4 w-4" />
                <span className="sr-only">Insert Image</span>
              </Button>
            )}

            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={cn(
                'h-8 w-8 rounded-md',
                editor.isActive('bulletList') ? 'bg-muted' : ''
              )}
            >
              <List className="h-4 w-4" />
              <span className="sr-only">Bullet List</span>
            </Button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
              aria-hidden="true"
            />
          </div>
        )}
      </div>

      {limit && (
        <div
          className={cn(
            'text-sm text-gray-500 text-right mt-2',
            getPlainTextLength(editor.getText()) > limit && 'text-red-500'
          )}
        >
          {getPlainTextLength(editor.getText())}/{limit} characters
        </div>
      )}

      {/* Sleek Link Modal - Two Rows with Labels */}
      {linkModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-400/60"
          onClick={closeLinkModal}
        >
          <div
            className="bg-white rounded-xl shadow-xl px-4 py-4 min-w-[360px] flex flex-col gap-3 relative"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}
          >
            <div className="flex flex-col gap-2 mb-2">
              <label
                className="text-sm font-medium mb-1"
                htmlFor="link-url-input"
              >
                Link URL
              </label>
              <input
                id="link-url-input"
                type="text"
                className="border rounded-lg px-3 py-2 w-full text-base"
                placeholder="Paste or type URL..."
                value={linkInput}
                autoFocus
                onChange={(e) => setLinkInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLinkConfirm();
                  if (e.key === 'Escape') closeLinkModal();
                }}
              />
            </div>
            <div className="flex flex-col gap-2 mb-2">
              <label
                className="text-sm font-medium mb-1"
                htmlFor="link-text-input"
              >
                Link Text
              </label>
              <input
                id="link-text-input"
                type="text"
                className="border rounded-lg px-3 py-2 w-full text-base"
                placeholder="Link text (optional)"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleLinkConfirm();
                  if (e.key === 'Escape') closeLinkModal();
                }}
              />
            </div>
            <div className="flex gap-2 justify-end mt-2">
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-full"
                title="Save"
                onClick={handleLinkConfirm}
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M5 12l5 5L20 7" />
                </svg>
              </button>
              <a
                href={linkInput || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-gray-100 rounded-full"
                title="Open link"
                style={{ color: '#6366f1' }}
              >
                <svg
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-full"
                title="Remove link"
                onClick={handleLinkRemove}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
